import type { WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { Status as BookingStatus, Type as BookingType } from '@brighterly/lib-core-types/booking/enums'

import { useListsStore } from '@shared/model'

import { LessonsSortDirection } from './enums'
import { useLessonsStore } from './store'
import { useDisciplineFiltersDraft } from '../discipline-filters-draft.hook'
import {
  LESSONS_DAYS_OF_WEEK,
  fromLessonsDateRange,
  lessonsFiltersToQuery,
  toLessonsDateRange,
} from '../../lib/lessons-query'

interface FiltersLessonsReturn {
  refDateRange: WritableComputedRef<Date[] | null>
  refTutorIds: WritableComputedRef<number[]>
  refStatuses: WritableComputedRef<string[]>
  refTypes: WritableComputedRef<string[]>
  refSortDirection: WritableComputedRef<string>
  refDayOfWeek: WritableComputedRef<string | undefined>
  statusOptions: ComputedRef<AppSelectOption<string>[]>
  typeOptions: ComputedRef<AppSelectOption<string>[]>
  sortOptions: AppSelectOption<string>[]
  dayOptions: AppSelectOption<string>[]
  isLoading: ComputedRef<boolean>
  clearFilters: () => void
  submitSearch: () => Promise<void>
}

const SORT_OPTIONS: AppSelectOption<string>[] = [
  { value: LessonsSortDirection.Desc, text: 'New to old' },
  { value: LessonsSortDirection.Asc, text: 'Old to new' },
]

const DAY_OPTIONS: AppSelectOption<string>[] = LESSONS_DAYS_OF_WEEK.map(day => ({ value: day, text: day }))

/** Every filter request is a list read, so the controls only prepare the filters: Search applies them and Clear
 *  filters resets them, as in legacy. */
export const useFiltersLessons = (): FiltersLessonsReturn => {
  const store = useLessonsStore()
  const listsStore = useListsStore()

  const { refDraft, isLoading, patchDraft, search, clearFilters } = useDisciplineFiltersDraft({
    applied: () => store.getFilters,
    onApply: filters => store.setFilters(filters),
    onReset: () => store.resetFilters(),
    toQuery: lessonsFiltersToQuery,
    loading: () => store.isLoading,
  })

  const refDateRange = computed<Date[] | null>({
    get: () => toLessonsDateRange(refDraft.value.from, refDraft.value.to),
    set: (value) => {
      patchDraft(fromLessonsDateRange(value))
    },
  })

  const refTutorIds = computed<number[]>({
    get: () => refDraft.value.tutorIds,
    set: (value) => {
      patchDraft({ tutorIds: value })
    },
  })

  const refStatuses = computed<string[]>({
    get: () => refDraft.value.statuses,
    set: (value) => {
      patchDraft({ statuses: value as BookingStatus[] })
    },
  })

  const refTypes = computed<string[]>({
    get: () => refDraft.value.types,
    set: (value) => {
      patchDraft({ types: value as BookingType[] })
    },
  })

  const refSortDirection = computed<string>({
    get: () => refDraft.value.sortDirection,
    set: (value) => {
      patchDraft({
        sortDirection: value === LessonsSortDirection.Asc ? LessonsSortDirection.Asc : LessonsSortDirection.Desc,
      })
    },
  })

  const refDayOfWeek = computed<string | undefined>({
    get: () => refDraft.value.dayOfWeek ?? undefined,
    set: (value) => {
      patchDraft({ dayOfWeek: value ?? null })
    },
  })

  const statusOptions = computed<AppSelectOption<string>[]>(() =>
    Object.entries(listsStore.getLists?.booking.status ?? {}).map(([value, text]) => ({ value, text })))

  const typeOptions = computed<AppSelectOption<string>[]>(() =>
    Object.entries(listsStore.getLists?.booking.type ?? {}).map(([value, text]) => ({ value, text })))

  return {
    refDateRange,
    refTutorIds,
    refStatuses,
    refTypes,
    refSortDirection,
    refDayOfWeek,
    statusOptions,
    typeOptions,
    sortOptions: SORT_OPTIONS,
    dayOptions: DAY_OPTIONS,
    isLoading,
    clearFilters,
    submitSearch: search,
  }
}
